import React, { useEffect, useMemo, useRef, useState } from "react";
import DashboardOutlinedIcon from "@mui/icons-material/DashboardOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { Button } from "components/common/Button";
import { DropdownMenu } from "components/common/DropdownMenu";
import { DropdownMenuItem } from "components/common/DropdownMenuItem";
import { SpaceDot } from "@/components/main/spaces/SpaceDot";
import {
  spaceColor,
  spaceSubtree,
  spaceTrail,
  useOrgContextStore,
  useVisibleSpacePaths,
  visibleSpaces,
  type SpacePath,
} from "@/components/main/stores/orgContextStore";
import { useRailOpen } from "@/components/main/utils/railOpen";

export const HOVER_DELAY_MS = 150;

/// Heads the Overview: the path back out, then the narrowed space as title;
/// with the rail collapsed, hovering either lists the spaces beneath — the
/// open rail already shows that tree. Not a `SectionRule`: it heads the page.
export const SpaceFilterRule: React.FC<{
  spaceId: string | null;
  onSelect: (spaceId: string | null) => void;
}> = ({ spaceId, onSelect }) => {
  const spaces = useOrgContextStore((s) => s.spaces);
  const paths = useVisibleSpacePaths();
  const railOpen = useRailOpen();
  const trail = useMemo(
    () => (spaceId ? spaceTrail(visibleSpaces(spaces), spaceId) : []),
    [spaces, spaceId],
  );
  if (spaceId && trail.length === 0) return null;
  const active = trail.at(-1) ?? null;
  const ancestors = trail.slice(0, -1);
  const menu = { paths, currentId: spaceId, onSelect, enabled: !railOpen };

  return (
    // The swatch sits in the title row so it centres on the name; the column
    // geometry is the section rules' (4px pad, 20px icon, 8px gap).
    <div
      className="grid items-center px-1"
      style={{
        gridTemplateColumns: "20px minmax(0, 1fr)",
        columnGap: "var(--spacing-sm)",
      }}
    >
      <span
        aria-hidden
        className="flex items-center justify-center text-fg-muted"
        style={{ gridColumn: 1, gridRow: 1 }}
      >
        <DashboardOutlinedIcon sx={{ fontSize: 14 }} />
      </span>
      <span
        className="inline-flex items-center gap-1.5"
        style={{ gridColumn: 2, gridRow: 1 }}
      >
        <SpacesBelowMenu rootId={null} label="All spaces" {...menu}>
          {active ? (
            <Button
              variant="quiet-link"
              size="xs"
              onClick={() => onSelect(null)}
              data-testid="clear-space-filter"
            >
              All spaces
            </Button>
          ) : (
            // The link's own box (a 1px border around normal leading), so the
            // row keeps its height as it turns into the link.
            <h1 className="border border-transparent leading-[normal] text-fg-semibold">
              All spaces
            </h1>
          )}
        </SpacesBelowMenu>
        {ancestors.map((space) => (
          <React.Fragment key={space.space_id}>
            <span aria-hidden className="text-fg-muted opacity-50">
              /
            </span>
            <SpacesBelowMenu
              rootId={space.space_id}
              label={space.name}
              {...menu}
            >
              <Button
                variant="quiet-link"
                size="xs"
                onClick={() => onSelect(space.space_id)}
              >
                {space.name}
              </Button>
            </SpacesBelowMenu>
          </React.Fragment>
        ))}
      </span>
      {active && (
        <span
          className="flex items-center justify-center"
          style={{ gridColumn: 1, gridRow: 2 }}
        >
          <SpaceDot color={spaceColor(active)} size="lg" />
        </span>
      )}
      {/* The title's line height either way, so the lists below never move. */}
      <div
        className="flex h-5 min-w-0 items-center"
        style={{ gridColumn: 2, gridRow: 2 }}
      >
        {active && (
          <SpacesBelowMenu
            rootId={active.space_id}
            label={active.name}
            {...menu}
          >
            <h1 className="min-w-0 truncate text-fg-title leading-tight">
              {active.name}
            </h1>
          </SpacesBelowMenu>
        )}
      </div>
    </div>
  );
};

const SpacesBelowMenu: React.FC<{
  rootId: string | null;
  label: string;
  paths: SpacePath[];
  currentId: string | null;
  onSelect: (spaceId: string) => void;
  enabled: boolean;
  children: React.ReactNode;
}> = ({ rootId, label, paths, currentId, onSelect, enabled, children }) => {
  const anchorRef = useRef<HTMLSpanElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const below = useMemo(() => spaceSubtree(paths, rootId), [paths, rootId]);
  useEffect(() => () => clearTimeout(timer.current), []);

  if (!enabled || below.length === 0) return <>{children}</>;

  const setOpenAfterDelay = (next: boolean) => {
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setIsOpen(next), HOVER_DELAY_MS);
  };

  return (
    <>
      <span
        ref={anchorRef}
        className="flex min-w-0 items-center"
        onMouseEnter={() => setOpenAfterDelay(true)}
        onMouseLeave={() => setOpenAfterDelay(false)}
        // The head outlives its crumb's click, so the menu would stay open.
        onClick={() => {
          clearTimeout(timer.current);
          setIsOpen(false);
        }}
      >
        {children}
      </span>
      <DropdownMenu
        anchorRef={anchorRef}
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        className="scrollbar-overlay-hover"
        // The rail flyouts' surface: the shared menu shadow is mixed from fg
        // and glows in dark mode.
        style={{
          width: "max-content",
          minWidth: "12rem",
          maxWidth: "20rem",
          maxHeight: "min(60vh, 24rem)",
          overflow: "auto",
          boxShadow: "none",
        }}
      >
        <div
          role="group"
          aria-label={rootId === null ? "All spaces" : `Spaces inside ${label}`}
          className="flex w-max min-w-full flex-col p-1"
          onMouseEnter={() => clearTimeout(timer.current)}
          onMouseLeave={() => setOpenAfterDelay(false)}
        >
          {isOpen &&
            below.map(({ path: { space }, depth }) => {
              const isCurrent = space.space_id === currentId;
              return (
                <DropdownMenuItem
                  key={space.space_id}
                  className={`group h-7! rounded-app ${isCurrent ? "bg-list-selected-light!" : ""}`}
                  onClick={() => {
                    setIsOpen(false);
                    onSelect(space.space_id);
                  }}
                >
                  <span
                    className="flex min-w-0 flex-1 items-center gap-1.5"
                    style={{
                      paddingLeft: `calc(${depth} * var(--spacing-md))`,
                    }}
                  >
                    <SpaceDot color={spaceColor(space)} />
                    <span
                      className={`min-w-0 max-w-40 flex-1 truncate text-left ${
                        isCurrent
                          ? "text-fg-regular"
                          : "text-fg-muted-regular group-hover:text-[var(--color-fg)]"
                      }`}
                    >
                      {space.name}
                    </span>
                    {space.sealed && (
                      <LockOutlinedIcon
                        sx={{ fontSize: 11 }}
                        className="shrink-0 text-fg-muted"
                      />
                    )}
                  </span>
                </DropdownMenuItem>
              );
            })}
        </div>
      </DropdownMenu>
    </>
  );
};
